<template>
    <section class="dp-layout">
        <section class="dp-left">
            <DepartmentPane directory-scope="teaching" @tree-node-click="treeNodeClick"></DepartmentPane>
        </section>

        <section class="dp-right">
            <div class="toolbar-filter-bar">
                <el-form :inline="true" size="small" class="toolbar-filter-form">
                    <div class="toolbar-filter-fields">
                        <el-form-item label="统计周期">
                            <el-radio-group v-model="periodType" size="small" @change="onPeriodTypeChange">
                                <el-radio-button label="week">周</el-radio-button>
                                <el-radio-button label="month">月</el-radio-button>
                                <el-radio-button label="semester">学期</el-radio-button>
                            </el-radio-group>
                        </el-form-item>
                        <el-form-item v-if="periodType === 'week' || periodType === 'semester'" label="学期">
                            <el-select v-model="filters.semesterId" placeholder="选择学期" class="toolbar-field-w-lg"
                                @change="onSemesterChange">
                                <el-option v-for="sem in semesterList" :key="sem.Id" :label="sem.SemesterName"
                                    :value="sem.Id" />
                            </el-select>
                        </el-form-item>
                        <el-form-item v-if="periodType === 'week'" label="周次">
                            <el-select v-model="filters.weekNo" placeholder="选择周次" class="toolbar-field-w-week"
                                @change="handleQuery">
                                <el-option v-for="w in weekOptions" :key="w.value" :label="w.label" :value="w.value" />
                            </el-select>
                        </el-form-item>
                        <el-form-item v-if="periodType === 'month'" label="月份">
                            <el-date-picker v-model="filters.yearMonth" type="month" placeholder="选择月份"
                                value-format="yyyy-MM" class="toolbar-field-w-md" @change="handleQuery" />
                        </el-form-item>
                        <el-form-item label="班级">
                            <el-input
                                v-model="filters.keyword"
                                clearable
                                placeholder="班级名称"
                                class="toolbar-field-w-md"
                                @keyup.enter.native="handleQuery"
                                @clear="handleQuery"
                            />
                        </el-form-item>
                        <el-form-item>
                            <el-checkbox v-model="filters.onlyInspected" @change="handleQuery">仅看有检查</el-checkbox>
                        </el-form-item>
                        <el-form-item>
                            <el-button type="primary" icon="el-icon-search" @click="handleQuery">查询</el-button>
                            <el-button type="warning" icon="el-icon-download" :loading="exporting"
                                @click="exportExcel">导出</el-button>
                        </el-form-item>
                    </div>
                </el-form>
            </div>

            <div class="tip-bar">
                <i class="el-icon-warning-outline"></i>
                <span>
                    左侧为<strong>家校通讯录</strong>：点校区/学段/年级/班级可缩小统计范围；无检查记录时仍列出范围内班级（明细为 0，保留初始分）。
                    本榜为宿舍检查班级口径，与值周总榜不同；多班混住勾选多班会各自记相同分。初始分换区间不加倍。
                </span>
            </div>

            <div class="tbl-scroll-host">
                <el-table :data="rows" v-loading="listLoading" height="100%" border style="width: 100%;">
                    <el-table-column prop="Rank" label="排名" min-width="70" align="center"></el-table-column>
                    <el-table-column prop="DepartmentName" label="班级" min-width="160" show-overflow-tooltip></el-table-column>
                    <el-table-column prop="InspectCount" label="检查次数" min-width="90" align="center"></el-table-column>
                    <el-table-column prop="InitialScore" label="初始分" min-width="90" align="center">
                        <template slot-scope="scope">{{ formatDecimal(scope.row.InitialScore) }}</template>
                    </el-table-column>
                    <el-table-column prop="DetailScore" label="宿舍明细净分" min-width="120" align="center">
                        <template slot-scope="scope">
                            <span :class="scoreClass(scope.row.DetailScore != null ? scope.row.DetailScore : scope.row.NetScore)">
                                {{ formatDecimal(scope.row.DetailScore != null ? scope.row.DetailScore : scope.row.NetScore) }}
                            </span>
                        </template>
                    </el-table-column>
                    <el-table-column prop="SyncedDutyScore" label="已同步值周净分" min-width="130" align="center">
                        <template slot-scope="scope">
                            <span :class="scoreClass(scope.row.SyncedDutyScore != null ? scope.row.SyncedDutyScore : scope.row.SyncedDutyNetScore)">
                                {{ formatDecimal(scope.row.SyncedDutyScore != null ? scope.row.SyncedDutyScore : scope.row.SyncedDutyNetScore) }}
                            </span>
                        </template>
                    </el-table-column>
                    <el-table-column prop="TotalScore" label="合计" min-width="100" align="center">
                        <template slot-scope="scope">
                            <strong>{{ formatDecimal(scope.row.TotalScore) }}</strong>
                        </template>
                    </el-table-column>
                </el-table>
            </div>

            <div class="pagination-container">
                <el-pagination
                    layout="total, sizes, prev, pager, next"
                    :total="total"
                    :current-page.sync="page"
                    :page-size.sync="pageSize"
                    :page-sizes="[20, 50, 100, 200]"
                    @size-change="onPageSizeChange"
                    @current-change="onPageChange"
                />
            </div>
        </section>
    </section>
</template>

<script>
import {
    getDormInspectClassRank,
    exportDormInspectClassRank,
} from '../../../api/modules/dormitory';
import { getSemesterList, getSemesterWeeksBySemesterId } from '../../../api/modules/misc';
import { resolveActiveSchoolId, toDeptId, treeNodeDeptId } from '../../../utils/deptCascaderProps';
import DepartmentPane from '../../../components/DepartmentPane';

export default {
    name: 'DormInspectClassRank',
    components: { DepartmentPane },
    data() {
        const now = new Date();
        const yearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        return {
            DepartmentId: 0,
            schoolDepartmentId: 0,
            scopeDepartmentId: 0,
            campusId: null,
            periodType: 'week',
            semesterList: [],
            weekOptions: [],
            filters: {
                semesterId: null,
                weekNo: null,
                yearMonth,
                keyword: '',
                onlyInspected: false,
            },
            rows: [],
            total: 0,
            page: 1,
            pageSize: 50,
            listLoading: false,
            exporting: false,
        };
    },
    methods: {
        scoreClass(val) {
            const n = Number(val);
            if (Number.isNaN(n) || n === 0) return '';
            return n > 0 ? 'add-value' : 'deduct-value';
        },
        formatDecimal(val) {
            if (val === null || val === undefined || val === '') return '';
            const n = Number(val);
            return Number.isNaN(n) ? val : n.toFixed(2);
        },
        /** 当前学校 Id（字符串）。最后修改：2026-10-18 */
        resolveSchoolId() {
            return toDeptId(this.schoolDepartmentId)
                || resolveActiveSchoolId(this)
                || toDeptId(this.DepartmentId);
        },
        treeNodeClick(data) {
            const nodeId = treeNodeDeptId(data);
            if (!nodeId) return;
            this.DepartmentId = nodeId;
            this.scopeDepartmentId = nodeId;
            const orgType = String(data.DeptOrgType || data.deptOrgType || '').toLowerCase();
            if (orgType === 'school') {
                this.schoolDepartmentId = nodeId;
                this.campusId = null;
            } else {
                this.schoolDepartmentId = toDeptId(data.schoolId || data.SchoolId) || resolveActiveSchoolId(this);
                if (orgType === 'campus') {
                    this.campusId = nodeId;
                } else {
                    this.campusId = toDeptId(data.campusId || data.CampusId) || null;
                }
            }
            this.loadSemesters().then(() => this.handleQuery());
        },
        async loadSemesters() {
            const schoolId = this.resolveSchoolId();
            if (!schoolId) return;
            const res = await getSemesterList({ departmentId: schoolId });
            const list = (res && res.response) || [];
            this.semesterList = list;
            if (list.length) {
                const current = list.find(s => s.IsCurrent === 1) || list[0];
                this.filters.semesterId = current.Id;
                await this.loadWeeks();
            }
        },
        async loadWeeks() {
            if (!this.filters.semesterId) {
                this.weekOptions = [];
                return;
            }
            try {
                const res = await getSemesterWeeksBySemesterId({ semesterId: this.filters.semesterId });
                const list = (res && res.data && res.data.response) || (res && res.response) || [];
                // 接口字段为 WeekNumber + Days[].Date（对齐值周/失物招领）
                this.weekOptions = (list || []).map(w => {
                    const weekNo = w.WeekNumber != null ? w.WeekNumber : (w.weekNumber != null ? w.weekNumber : null);
                    let firstDate = '';
                    let lastDate = '';
                    if (Array.isArray(w.Days) && w.Days.length) {
                        firstDate = String(w.Days[0].Date || '').split(' ')[0];
                        lastDate = String(w.Days[w.Days.length - 1].Date || '').split(' ')[0];
                    } else {
                        firstDate = String(w.StartDate || w.startDate || '').substring(0, 10);
                        lastDate = String(w.EndDate || w.endDate || '').substring(0, 10);
                    }
                    const label = firstDate && lastDate
                        ? `第${weekNo}周 (${firstDate} ~ ${lastDate})`
                        : `第${weekNo}周`;
                    return { value: weekNo, label, startDate: firstDate, endDate: lastDate };
                }).filter(w => w.value != null);

                // 默认周次：优先包含今天的周，否则第一周（对齐值周统计）
                if (this.weekOptions.length) {
                    const todayStr = this.formatLocalDate(new Date());
                    const currentWeek = this.weekOptions.find(w =>
                        w.startDate && w.endDate && todayStr >= w.startDate && todayStr <= w.endDate
                    );
                    this.filters.weekNo = currentWeek ? currentWeek.value : this.weekOptions[0].value;
                } else {
                    this.filters.weekNo = null;
                }
            } catch (e) {
                this.weekOptions = [];
            }
        },
        /** 本地日期 yyyy-MM-dd */
        formatLocalDate(date) {
            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const d = String(date.getDate()).padStart(2, '0');
            return `${y}-${m}-${d}`;
        },
        onPeriodTypeChange() {
            this.handleQuery();
        },
        async onSemesterChange() {
            await this.loadWeeks();
            this.handleQuery();
        },
        buildParams() {
            const schoolId = this.resolveSchoolId();
            const params = {
                schoolId,
                departmentId: schoolId,
                scopeDepartmentId: this.scopeDepartmentId || undefined,
                campusId: this.campusId || undefined,
                periodType: this.periodType,
                page: this.page,
                intPageSize: this.pageSize,
                keyword: (this.filters.keyword || '').trim() || undefined,
                onlyInspected: !!this.filters.onlyInspected,
            };
            if (this.periodType === 'week') {
                params.semesterId = this.filters.semesterId;
                params.weekNo = this.filters.weekNo;
                const week = this.weekOptions.find(w => String(w.value) === String(this.filters.weekNo));
                if (week) {
                    params.startDate = week.startDate && String(week.startDate).substring(0, 10);
                    params.endDate = week.endDate && String(week.endDate).substring(0, 10);
                }
            } else if (this.periodType === 'month') {
                params.yearMonth = this.filters.yearMonth;
                if (this.filters.yearMonth) {
                    const [y, m] = this.filters.yearMonth.split('-').map(Number);
                    params.startDate = `${this.filters.yearMonth}-01`;
                    const lastDay = new Date(y, m, 0).getDate();
                    params.endDate = `${this.filters.yearMonth}-${String(lastDay).padStart(2, '0')}`;
                }
            } else {
                params.semesterId = this.filters.semesterId;
                const sem = this.semesterList.find(s => s.Id === this.filters.semesterId);
                if (sem) {
                    params.startDate = sem.StartDate && String(sem.StartDate).substring(0, 10);
                    params.endDate = sem.EndDate && String(sem.EndDate).substring(0, 10);
                }
            }
            return params;
        },
        handleQuery() {
            this.page = 1;
            this.loadList();
        },
        loadList() {
            const schoolId = this.resolveSchoolId();
            if (!schoolId) return;
            this.listLoading = true;
            getDormInspectClassRank(this.buildParams()).then(res => {
                if (res.data && res.data.success) {
                    const resp = res.data.response || {};
                    this.rows = Array.isArray(resp) ? resp : (resp.data || []);
                    this.total = Array.isArray(resp) ? this.rows.length : (resp.dataCount || this.rows.length);
                } else {
                    this.rows = [];
                    this.total = 0;
                    this.$message.error((res.data && res.data.msg) || '加载失败');
                }
            }).finally(() => {
                this.listLoading = false;
            });
        },
        onPageChange(page) {
            this.page = page;
            this.loadList();
        },
        onPageSizeChange(size) {
            this.pageSize = size;
            this.page = 1;
            this.loadList();
        },
        async exportExcel() {
            try {
                this.exporting = true;
                const params = { ...this.buildParams() };
                delete params.page;
                delete params.intPageSize;
                const response = await exportDormInspectClassRank(params);
                if (!response.data) {
                    this.$message.error('导出数据为空');
                    return;
                }
                const blob = new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `宿舍检查-班级统计-${this.periodType}.xlsx`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            } catch (e) {
                this.$message.error('导出失败');
            } finally {
                this.exporting = false;
            }
        },
    },
};
</script>

<style scoped>
.tip-bar {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin: 0 0 12px;
    padding: 8px 12px;
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 6px;
    color: #78716c;
    font-size: 13px;
    line-height: 1.5;
}
.tip-bar i { color: #d97706; margin-top: 2px; }
.add-value { color: #67c23a; }
.deduct-value { color: #f56c6c; }
.pagination-container {
    margin-top: 12px;
    overflow: hidden;
    text-align: right;
}
</style>
